import { VERSION as CDK_VERSION } from '@angular/cdk';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core';
import { VERSION as MAT_VERSION } from '@angular/material/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { bootstrapApplication, HammerModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { isSurveyActionLog } from '@damofire/data-commons';
import { PHOTO_ANNOTATION_FLATTENER } from '@damofire/data-nexus';
import { flattenAnnotationsOntoImage, UserAnnotation } from '@damofire/uix-commons';
import { provideDamoAuth } from '@damotech/damoauth-lib';
import { provideTranslateService, TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import Aura from '@primeuix/themes/aura';
import { createErrorHandler, init, TraceService } from '@sentry/angular';
import { NgxEchartsModule } from 'ngx-echarts';
import packageInfo from 'package.json';
import { providePrimeNG } from 'primeng/config';
import { ApiModule } from './api/api.module';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { isBannedError, prepareIntegrations } from './app/app.module';
import { CommonApiModule } from './app/common/common.module';
import { RouterInterceptorService } from './app/common/service/router-interceptor.service';
import { appFirebaseConfig } from './app/firebase/app-firebase.module';
import { SecurityModule } from './app/security/security.module';
import { environment } from './environments/environment';
import { provideCustomIcons } from './app/common/custom-icons';
import { BATCH_REPAIR_PROGRESS_CARD } from './app/watson/issues/batch-repair/batch-repair-progress-card.token';
import { BatchRepairProgressCardComponent } from './app/watson/issues/batch-repair/progress-card/batch-repair-progress-card.component';

if (environment.production) {
  enableProdMode();
}

const integrations = prepareIntegrations(environment.production);

console.log(`Using damofire@${packageInfo.version}`);
console.log('Angular CDK version', CDK_VERSION.full);
console.log('Angular Material version', MAT_VERSION.full);

init({
  dsn: 'https://567148f1745f4f589e65d2d72b133f18@o509349.ingest.sentry.io/5603863',
  environment: environment.name,
  integrations,
  tracesSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  release: `damofire@${packageInfo.version}`,
  tracePropagationTargets: [environment.appConfig.fns],
  normalizeDepth: 10,
  enableLogs: true,
  beforeSendLog(log) {
    if (!environment.production) {
      console.log(`[log] ${log.level} ${log.message}`, log.attributes ?? {});
      return null;
    }
    if (isSurveyActionLog(log.level, log.attributes)) {
      return log;
    }
    if (log.level !== 'warn' && log.level !== 'error' && log.level !== 'fatal') {
      return null;
    }
    return log;
  },
  beforeSend(event, hint) {
    const exception = hint.originalException || (event?.extra?.err as Error) || event?.message;
    if (!environment.production) {
      return null;
    }

    if (isBannedError(exception)) {
      return null;
    }
    return event;
  },
});

document.addEventListener('DOMContentLoaded', () => {
  bootstrapApplication(AppComponent, {
    providers: [
      provideHttpClient(withInterceptorsFromDi()),
      provideTranslateService({ fallbackLang: 'en', extend: true }),
      ...provideTranslateHttpLoader({ prefix: 'assets/i18n/', suffix: '.json' }),
      importProvidersFrom(
        HammerModule,
        TranslateModule,
        AppRoutingModule,
        ApiModule.forRoot(),
        CommonApiModule.forRoot(),
        SecurityModule.forRoot(),
        ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.target !== 'app' && environment.name !== 'local' }),
        NgxEchartsModule.forRoot({ echarts: () => import('echarts') }),
      ),
      ...appFirebaseConfig.providers,
      { provide: ErrorHandler, useValue: createErrorHandler({ showDialog: false }) },
      { provide: TraceService, deps: [Router] },
      { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 3000 } },
      {
        provide: PHOTO_ANNOTATION_FLATTENER,
        useValue: (imageSrc: string, annotations: unknown[] | undefined) => flattenAnnotationsOntoImage(imageSrc, annotations as UserAnnotation[] | undefined),
      },
      RouterInterceptorService,
      { provide: BATCH_REPAIR_PROGRESS_CARD, useValue: BatchRepairProgressCardComponent },
      provideAnimations(),
      provideCustomIcons(),
      providePrimeNG({
        theme: {
          preset: Aura,
          options: {
            darkModeSelector: false,
          },
        },
      }),
      provideDamoAuth({
        firebase: environment.firebase,
        fns: environment.appConfig.fns,
        domain: environment.appConfig.domain,
        fnsOrigin: environment.appConfig.ngFnsOrigin,
      }),
    ],
  }).catch((err) => console.error(err));
});
